import React from "react";
import { Box, Grid, TextField, MenuItem, Typography, Tooltip, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Download } from "@mui/icons-material";
import DownloadingIcon from "@mui/icons-material/Downloading";

const getFieldSx = (fieldName, saveStatus) => ({
  ...fieldStyles,
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor:
        saveStatus[fieldName] === "success"
          ? "green"
          : saveStatus[fieldName] === "error"
            ? "red"
            : undefined,
    },
    "& fieldset": {
      borderColor:
        saveStatus[fieldName] === "success"
          ? "green"
          : saveStatus[fieldName] === "error"
            ? "red"
            : undefined,
    },
  },
});

const IdentityPersonalInfoForm = ({
  employeeDetails,
  setEmployeeDetails,
  saveStatus,
  handleFieldSave,
  handleUploadCV,
  handleDownloadCV,
  employeeName,
  selectedEmployeeId,
  cvExists,
  downloadLoading,
}) => {
  const { t } = useTranslation();

  // Options for dropdowns
  const martialStatusOptions = [
    {
      value: "Single",
      label: t("admin.employees.employeeDialog.employeeDetails.martialStatusSingle"),
    },
    {
      value: "Married",
      label: t("admin.employees.employeeDialog.employeeDetails.martialStatusMarried"),
    },
  ];

  const militaryStatusOptions = [
    {
      value: t("admin.employees.employeeDialog.employeeDetails.militaryOption1"),
      label: t("admin.employees.employeeDialog.employeeDetails.militaryOption1"),
    },
    {
      value: t("admin.employees.employeeDialog.employeeDetails.militaryOption2"),
      label: t("admin.employees.employeeDialog.employeeDetails.militaryOption2"),
    },
    {
      value: t("admin.employees.employeeDialog.employeeDetails.militaryOption3"),
      label: t("admin.employees.employeeDialog.employeeDetails.militaryOption3"),
    },
    {
      value: t("admin.employees.employeeDialog.employeeDetails.militaryOption4"),
      label: t("admin.employees.employeeDialog.employeeDetails.militaryOption4"),
    },
  ];

  const religionOptions = [
    {
      value: t("admin.employees.employeeDialog.employeeDetails.religionMuslim"),
      label: t("admin.employees.employeeDialog.employeeDetails.religionMuslim"),
    },
    {
      value: t("admin.employees.employeeDialog.employeeDetails.religionChristian"),
      label: t("admin.employees.employeeDialog.employeeDetails.religionChristian"),
    },
    // Add more religions if needed
  ];

  return (
    <Box mt={0}>
      <Grid item xs={12} mt={2}>
        <Typography variant="h5">
          {t("admin.employees.employeeDialog.employeeDetails.cv")}
        </Typography>
      </Grid>

      <Grid container spacing={2}>
        <Grid item>
          <input
            type="file"
            id={`file-input-${employeeDetails.id}`}
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              await handleUploadCV(file); // call parent function
            }}
          />
          <Tooltip title={t("admin.employees.employeeDialog.employeeDetails.upload")} arrow>
            <IconButton
              component="span"
              onClick={() => document.getElementById(`file-input-${employeeDetails.id}`).click()}
              size="small"
              color={cvExists ? "success" : "default"}
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>

          {cvExists && (
            <Tooltip title={t("admin.employees.employeeDialog.employeeDetails.download")} arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleDownloadCV(selectedEmployeeId, employeeName)} // call parent download function
              >
                {/* <Download /> */}
                {downloadLoading ? <DownloadingIcon /> : <Download />}
              </IconButton>
            </Tooltip>
          )}
          {/* {cvFileName && (
              <Typography variant="body2" display="inline" ml={1}>
                {cvFileName}
              </Typography>
            )} */}
        </Grid>
      </Grid>

      <Typography variant="h5" mb={2} mt={2}>
        {t("admin.employees.employeeDialog.employeeDetails.identityInfo")}
      </Typography>

      <Grid container spacing={2}>
        {/* Date of Birth */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label={t("admin.employees.employeeDialog.employeeDetails.dob")}
            InputLabelProps={{ shrink: true }}
            value={employeeDetails.dateOfBirth || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, dateOfBirth: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(
                employeeDetails.id, // employee ID
                "dateOfBirth", // field name
                { dateOfBirth: e.target.value } // object matching DTO structure
              )
            }
            // sx={{ ...fieldStyles }}
            sx={getFieldSx("dateOfBirth", saveStatus)}
          />
        </Grid>

        {/* Nationality */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.nationality")}
            value={employeeDetails.nationality || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, nationality: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "nationality", {
                nationality: e.target.value,
              })
            }
            sx={getFieldSx("nationality", saveStatus)}
          />
        </Grid>

        {/* Military Status */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.militaryStatus")}
            value={employeeDetails.militaryStatus || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, militaryStatus: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "militaryStatus", {
                militaryStatus: e.target.value,
              })
            }
            sx={getFieldSx("militaryStatus", saveStatus)}
          >
            {militaryStatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Martial Status */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.martialStatus")}
            value={employeeDetails.martialStatus || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, martialStatus: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "martialStatus", {
                martialStatus: e.target.value,
              })
            }
            sx={getFieldSx("martialStatus", saveStatus)}
          >
            {martialStatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* National ID */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.nationalId")}
            value={employeeDetails.nationalId || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, nationalId: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "nationalId", {
                nationalId: e.target.value,
              })
            }
            sx={getFieldSx("nationalId", saveStatus)}
          />
        </Grid>

        {/* Passport Number */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.passportNum")}
            value={employeeDetails.passportNumber || ""}
            onChange={(e) =>
              setEmployeeDetails((prev) => ({ ...prev, passportNumber: e.target.value }))
            }
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "passportNumber", {
                passportNumber: e.target.value,
              })
            }
            sx={getFieldSx("passportNumber", saveStatus)}
          />
        </Grid>

        {/* Religion */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label={t("admin.employees.employeeDialog.employeeDetails.religion")}
            value={employeeDetails.religion || ""}
            onChange={(e) => setEmployeeDetails((prev) => ({ ...prev, religion: e.target.value }))}
            onBlur={(e) =>
              handleFieldSave(employeeDetails.id, "religion", { religion: e.target.value })
            }
            sx={getFieldSx("religion", saveStatus)}
          >
            {religionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Box>
  );
};

IdentityPersonalInfoForm.propTypes = {
  employeeDetails: PropTypes.object.isRequired,
  setEmployeeDetails: PropTypes.func.isRequired,
  saveStatus: PropTypes.object.isRequired,
  handleFieldSave: PropTypes.func.isRequired,
  handleUploadCV: PropTypes.func.isRequired,
  handleDownloadCV: PropTypes.func.isRequired,
  employeeName: PropTypes.string,
  selectedEmployeeId: PropTypes.string,
  cvExists: PropTypes.bool,
  downloadLoading: PropTypes.bool,
};

export default IdentityPersonalInfoForm;
